import React from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const AGB = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>AGB | ICG Hannover</title>
        <meta name="description" content="Allgemeine Geschäftsbedingungen der Internationalen Christengemeinde Hannover für die Nutzung der Website." />
        <link rel="canonical" href="https://www.icg-hannover.de/agb" />
      </Helmet>
      <header className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate('/')}
            data-testid="agb-back-btn"
            className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </button>
          <div className="flex items-center gap-4">
            <FileText className="w-10 h-10" />
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold">Allgemeine Geschäftsbedingungen</h1>
              <p className="text-white/70 mt-1">Internationale Christengemeinde Hannover</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 space-y-8 text-gray-700 leading-relaxed" data-testid="agb-content">

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 1 Geltungsbereich</h2>
            <p>
              Diese Allgemeinen Geschäftsbedingungen (nachfolgend „AGB") gelten für die Nutzung der Website der 
              Internationalen Christengemeinde Hannover (nachfolgend „ICG Hannover" oder „wir") unter der Domain 
              www.icg-hannover.de sowie für alle damit verbundenen digitalen Angebote und Dienste.
            </p>
            <p className="mt-3">
              Mit der Nutzung unserer Website erklären Sie sich mit diesen AGB einverstanden. Sollten Sie mit 
              einzelnen Bestimmungen nicht einverstanden sein, bitten wir Sie, die Website nicht weiter zu nutzen.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 2 Betreiber der Website</h2>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="font-semibold text-gray-900">Internationale Christengemeinde Hannover</p>
              <p>Am Klagesmarkt 29</p>
              <p>30159 Hannover</p>
              <p>Deutschland</p>
              <p className="mt-3">E-Mail: kontakt@icg-hannover.de</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 3 Leistungsbeschreibung</h2>
            <p>
              Die ICG Hannover stellt auf ihrer Website folgende Dienste und Inhalte bereit:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
              <li>Informationen über die Gemeinde, Gottesdienste und Veranstaltungen</li>
              <li>Zugang zu Predigten in Audio- und Videoformat</li>
              <li>Live-Stream-Übertragung von Gottesdiensten</li>
              <li>Tägliche Bibelverse</li>
              <li>Ein Kontaktformular zur Kommunikation mit der Gemeindeleitung</li>
              <li>Einen Newsletter-Service für regelmäßige Updates</li>
            </ul>
            <p className="mt-3">
              Die Nutzung dieser Dienste ist grundsätzlich kostenlos. Die ICG Hannover behält sich das Recht vor, 
              einzelne Dienste jederzeit ohne Vorankündigung zu ändern, zu ergänzen oder einzustellen.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 4 Nutzung der Website</h2>
            <p>
              Die Inhalte dieser Website dienen ausschließlich der Information über die ICG Hannover und ihre 
              Aktivitäten. Jede darüber hinausgehende Nutzung bedarf der vorherigen schriftlichen Zustimmung.
            </p>
            <p className="mt-3">Die Nutzer verpflichten sich insbesondere:</p>
            <ul className="list-disc list-inside mt-2 space-y-2 ml-4">
              <li>Die Website nicht für rechtswidrige Zwecke zu nutzen</li>
              <li>Keine Inhalte zu übermitteln, die beleidigend, diskriminierend oder anderweitig anstößig sind</li>
              <li>Keine automatisierten Abfragen oder Bots einzusetzen, die die Website beeinträchtigen könnten</li>
              <li>Die Rechte Dritter, insbesondere Urheberrechte und Persönlichkeitsrechte, zu achten</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 5 Kontaktformular und Newsletter</h2>

            <h3 className="text-lg font-semibold text-gray-800 mb-2">Kontaktformular</h3>
            <p>
              Über das Kontaktformular können Sie Nachrichten an die Gemeindeleitung senden. Die von Ihnen 
              eingegebenen Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet und gemäß unserer 
              Datenschutzerklärung verarbeitet. Ein Anspruch auf Beantwortung besteht nicht, wir bemühen uns 
              jedoch, alle Anfragen zeitnah zu bearbeiten.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">Newsletter</h3>
            <p>
              Mit der Anmeldung zu unserem Newsletter erklären Sie sich damit einverstanden, regelmäßig 
              Informationen über die Aktivitäten der ICG Hannover per E-Mail zu erhalten. Sie können sich 
              jederzeit vom Newsletter abmelden. Die Abmeldung kann über den entsprechenden Link in jeder 
              Newsletter-E-Mail oder durch eine Nachricht an unsere E-Mail-Adresse erfolgen.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 6 Geistiges Eigentum und Urheberrecht</h2>
            <p>
              Alle auf dieser Website veröffentlichten Inhalte (Texte, Bilder, Grafiken, Videos, Audiodateien, 
              Logos und sonstige Medien) sind urheberrechtlich geschützt. Die Vervielfältigung, Bearbeitung, 
              Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts bedürfen der 
              vorherigen schriftlichen Zustimmung der ICG Hannover oder des jeweiligen Rechteinhabers.
            </p>
            <p className="mt-3">
              Das Herunterladen und Kopieren von Predigtinhalten ist ausschließlich für den privaten, nicht 
              kommerziellen Gebrauch gestattet. Eine Weitergabe an Dritte zu kommerziellen Zwecken ist untersagt.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 7 Haftungsbeschränkung</h2>
            <p>
              Die Inhalte dieser Website werden mit größtmöglicher Sorgfalt erstellt. Die ICG Hannover übernimmt 
              jedoch keine Gewähr für die Richtigkeit, Vollständigkeit und Aktualität der bereitgestellten Inhalte.
            </p>
            <p className="mt-3">
              Die Nutzung der Website erfolgt auf eigene Gefahr des Nutzers. Die ICG Hannover haftet nicht für 
              Schäden, die durch die Nutzung oder Nichtnutzung der auf der Website angebotenen Informationen 
              entstehen, sofern kein nachweislich vorsätzliches oder grob fahrlässiges Verschulden vorliegt.
            </p>
            <p className="mt-3">
              Für externe Links übernimmt die ICG Hannover keine Verantwortung. Die Inhalte verlinkter Seiten 
              liegen in der alleinigen Verantwortung des jeweiligen Betreibers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 8 Verfügbarkeit der Website</h2>
            <p>
              Die ICG Hannover bemüht sich, die Website möglichst unterbrechungsfrei zur Verfügung zu stellen. 
              Ein Anspruch auf ständige Verfügbarkeit besteht jedoch nicht. Die ICG Hannover behält sich vor, 
              die Website für Wartungsarbeiten oder aus sonstigen Gründen vorübergehend oder dauerhaft einzustellen.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 9 Datenschutz</h2>
            <p>
              Der Schutz Ihrer persönlichen Daten ist uns wichtig. Einzelheiten zur Erhebung, Verarbeitung und 
              Nutzung Ihrer personenbezogenen Daten finden Sie in unserer{' '}
              <a href="/datenschutz" className="text-blue-600 hover:underline">
                Datenschutzerklärung
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 10 Änderungen der AGB</h2>
            <p>
              Die ICG Hannover behält sich das Recht vor, diese AGB jederzeit zu ändern. Die jeweils aktuelle 
              Fassung ist auf dieser Website einsehbar. Durch die fortgesetzte Nutzung der Website nach einer 
              Änderung der AGB erklären Sie sich mit den geänderten Bedingungen einverstanden.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 11 Salvatorische Klausel</h2>
            <p>
              Sollte eine Bestimmung dieser AGB unwirksam oder undurchführbar sein oder werden, so wird die 
              Wirksamkeit der übrigen Bestimmungen davon nicht berührt. Anstelle der unwirksamen Bestimmung gilt 
              eine wirksame Bestimmung als vereinbart, die dem wirtschaftlichen Zweck der unwirksamen Bestimmung 
              am nächsten kommt.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">§ 12 Anwendbares Recht und Gerichtsstand</h2>
            <p>
              Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist Hannover, sofern der Nutzer 
              Kaufmann, eine juristische Person des öffentlichen Rechts oder ein öffentlich-rechtliches 
              Sondervermögen ist.
            </p>
          </section>

          <section className="bg-blue-50 rounded-xl p-6">
            <p className="text-sm text-gray-600">
              <strong>Stand:</strong> März 2026
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Internationale Christengemeinde Hannover | Am Klagesmarkt 29 | 30159 Hannover
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default AGB;
